import { translate, useLocale } from "../../localization";
import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  MenuItem,
} from "@mui/material";
import { AppButton, AppTextField } from "../ui";
import {
  buildElementOptimizationPayload,
  createDefaultElementOptimizationForm,
  ELEMENT_OPTIMIZATION_METHOD_OPTIONS,
  ELEMENT_OPTIMIZATION_MODE_OPTIONS,
  getElementOptimizationModeHelperText,
  isCustomElementOptimizationMode,
  isValidElementOptimizationForm,
} from "../../utility/elementOptimization";
import {
  getSupportedElementFileHelperText,
  SUPPORTED_ELEMENT_FILE_INPUT_ACCEPT,
  isSupportedElementFile,
} from "../../utility/elementFiles";
import usePasteFilesOnHover from "./usePasteFilesOnHover";
import "./UploadElementDialog.scss";

const getFileKey = (file) => `${file.name}::${file.size}::${file.lastModified}`;

const BulkUploadElementDialog = ({ open, onClose, onUpload }) => {
  useLocale();
  const [namePrefix, setNamePrefix] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [optimization, setOptimization] = useState(
    createDefaultElementOptimizationForm()
  );
  const [isDragActive, setIsDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setNamePrefix("");
    setSelectedFiles([]);
    setOptimization(createDefaultElementOptimizationForm());
    setIsDragActive(false);
  };

  const addFiles = (fileList) => {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const incomingFiles = Array.from(fileList);
    const supportedFiles = incomingFiles.filter((file) => isSupportedElementFile(file));
    const unsupportedFiles = incomingFiles.filter((file) => !isSupportedElementFile(file));

    if (unsupportedFiles.length > 0) {
      const unsupportedNames = unsupportedFiles.map((file) => file.name).join(", ");
      alert(translate("Unsupported file type for: {value0}. {value1}", {
  value0: unsupportedNames,
  value1: getSupportedElementFileHelperText()
}));
    }

    if (supportedFiles.length === 0) {
      return;
    }

    setSelectedFiles((prev) => {
      const dedupedFiles = new Map();
      [...prev, ...supportedFiles].forEach((file) => {
        dedupedFiles.set(getFileKey(file), file);
      });
      return Array.from(dedupedFiles.values());
    });
  };

  const pasteHandlers = usePasteFilesOnHover({
    enabled: open && !loading,
    onFiles: (files) => {
      addFiles(files);
    },
  });

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget === e.target) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleClickDropzone = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadClick = async () => {
    if (
      selectedFiles.length === 0 ||
      loading ||
      !isValidElementOptimizationForm(optimization)
    ) {
      return;
    }

    setLoading(true);
    try {
      await onUpload({
        namePrefix,
        files: selectedFiles,
        optimization: buildElementOptimizationPayload(optimization),
      });
      resetState();
    } catch (error) {
      console.error("Bulk upload failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) {
      return;
    }
    resetState();
    onClose();
  };

  const fileSummaryText =
    selectedFiles.length === 0
      ? translate("Drag and drop files here, or click to select files")
      : translate("{value0} files selected", {
  value0: selectedFiles.length
});

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "upload-element-dialog__paper" }}
    >
      <DialogTitle className="upload-element-dialog__title">{translate("Bulk Upload Elements")}</DialogTitle>
      <DialogContent className="upload-element-dialog__content">
        <Grid container spacing={2} className="upload-element-dialog__form">
          <Grid item xs={12}>
            <AppTextField
              label={translate("Name Prefix")}
              value={namePrefix}
              onChange={(e) => setNamePrefix(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <AppTextField
              select
              label={translate("Optimization")}
              value={optimization.mode}
              onChange={(e) =>
                setOptimization((prev) => ({ ...prev, mode: e.target.value }))
              }
              helperText={getElementOptimizationModeHelperText(optimization.mode)}
              fullWidth
            >
              {ELEMENT_OPTIMIZATION_MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </AppTextField>
          </Grid>
          {isCustomElementOptimizationMode(optimization.mode) && (
            <>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  select
                  label={translate("Method")}
                  value={optimization.method}
                  onChange={(e) =>
                    setOptimization((prev) => ({
                      ...prev,
                      method: e.target.value,
                    }))
                  }
                  fullWidth
                >
                  {ELEMENT_OPTIMIZATION_METHOD_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </AppTextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  label={translate("Target Faces")}
                  type="number"
                  value={optimization.targetFaces}
                  onChange={(e) =>
                    setOptimization((prev) => ({
                      ...prev,
                      targetFaces: e.target.value,
                    }))
                  }
                  inputProps={{ min: 1, step: 1000 }}
                  helperText={translate("Positive integer face target.")}
                  fullWidth
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Box
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleClickDropzone}
              {...pasteHandlers}
              className={`upload-element-dialog__dropzone ${isDragActive ? "upload-element-dialog__dropzone--active" : ""}`}
            >
              <Typography className="upload-element-dialog__dropzone-text">
                {fileSummaryText}
              </Typography>
              <input
                type="file"
                accept={SUPPORTED_ELEMENT_FILE_INPUT_ACCEPT}
                multiple
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileInputChange}
              />
            </Box>
            {selectedFiles.length > 0 && (
              <div className="upload-element-dialog__file-list">
                {selectedFiles.map((file) => (
                  <Typography key={getFileKey(file)} className="upload-element-dialog__file-item">
                    {file.name}
                  </Typography>
                ))}
              </div>
            )}
            <Typography variant="caption" className="upload-element-dialog__naming-help">{translate("Element names use `prefix + filename`; description uses the filename.")}</Typography>
            <Typography variant="caption" className="upload-element-dialog__helper-text">
              {getSupportedElementFileHelperText()}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="upload-element-dialog__actions">
        <AppButton
          kind="ghost"
          onClick={handleClose}
          disabled={loading}
          className="upload-element-dialog__cancel"
        >{translate("Cancel")}</AppButton>
        <AppButton
          onClick={handleUploadClick}
          loading={loading}
          loadingLabel={translate("Uploading {value0} files", {
  value0: selectedFiles.length
})}
          disabled={
            selectedFiles.length === 0 || !isValidElementOptimizationForm(optimization)
          }
        >
          {selectedFiles.length > 0
            ? translate("Create & Upload {value0} Elements", {
  value0: selectedFiles.length
})
            : translate("Create & Upload Elements")}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadElementDialog;
